<template>
  <div v-if="!!tour" class="case-setup-container mt-24">
    <div v-if="hasSpecialNeeds" class="alert alert-danger mb-12 py-8 px-16" role="alert">
      <div class="d-flex align-items-center mb-4">
        <v-icon icon="info" size="xs" space="me-8" />
        <div class="fw-bold text-xs">
          {{ $t("clientTour.specialNeedsAlertTitle") || 'Special Needs & Requests' }}
        </div>
      </div>
      <ul class="mb-0 text-xs ps-24">
        <li v-if="!!clientTour?.additionalRequirements" v-html="clientTour.additionalRequirements" class="mb-4"></li>
        <li v-if="parsedSpecialRequests" class="mb-4">
          <strong>Special Requests:</strong> {{ parsedSpecialRequests }}
        </li>
        <li v-if="parsedTransferNeeds">
          <strong>Transfer Needs:</strong> {{ parsedTransferNeeds }}
        </li>
      </ul>
    </div>

    <div class="wrapper grid">
      <div class="grid left">
        <div id="accordionDetails" class="accordion">
          <div class="case-details accordion-item">
            <h2 id="headingDetails" class="accordion-header">
              <button
                aria-controls="collapseDetails"
                aria-expanded="true"
                class="accordion-button"
                data-bs-target="#collapseDetails"
                data-bs-toggle="collapse"
                type="button"
              >
                <div class="mb-0 d-flex align-items-center">
                  <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
                    <v-icon icon="pencil" size="xs" class="text-white" />
                  </div>
                  <div>
                    <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.details") }}</div>
                    <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.detailsSubtitle") }}</div>
                  </div>
                </div>
              </button>
            </h2>

            <div
              id="collapseDetails"
              aria-labelledby="headingDetails"
              class="accordion-collapse collapse show"
              data-bs-parent="#accordionDetails"
            >
              <div class="accordion-body pb-16 pb-xl-24">
                <form id="case-update-form" class="d-flex flex-column justify-content-between h-100">
                  <fieldset>
                    <legend class="visually-hidden">{{ $t("tourClient.basicInformation") }}</legend>

                    <div class="text-xs mb-12">
                      <span class="fw-medium me-8">{{ $t("general.shared.client") }}:</span>
                      <button
                        class="btn btn-link shadow-none text-xs p-0 fw-bold"
                        type="button"
                        @click="openOffCanvas('the-client-details-offcanvas')"
                      >
                        {{ clientName }}
                      </button>
                    </div>

                    <div v-if="clientTour?.client?.type === 'agency'" class="mb-12">
                      <v-form
                        id="case-update-party-name"
                        :label="`${$t('clientTour.partyName')}*`"
                        :yup-errors-variable="errors.partyName"
                        form-type="outline"
                        class="mb-0"
                      >
                        <v-form-input
                          id="case-update-party-name-input"
                          v-model="partyName"
                          form-type="outline"
                          autocomplete="off"
                          class="mb-0"
                        />
                      </v-form>
                    </div>

                    <div class="text-xs mb-12" v-if="clientTour?.client?.billing_country">
                      <span class="fw-medium me-8">{{ $t("general.shared.addressCountry") }}:</span>
                      <span class="fw-bold text-dark">{{ clientTour?.client?.billing_country }}</span>
                    </div>

                    <div class="text-xs mb-12" v-if="clientTour?.parent_id">
                      <span class="fw-medium me-8">
                        {{ $t("general.originated") }}
                        <a
                          :href="originatedLink"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="fw-bold text-decoration-underline text-primary"
                        >
                          ( {{ clientTour?.parent_id }} )
                        </a>
                      </span>
                    </div>

                    <v-select-user-case-search
                      id="case-update-user-search"
                      v-model="userId"
                      v-if="!clientTour.case_manager.agency?.is_collaborator"
                      :agency="agency.slug"
                      @update:model-value="assignUserToCase"
                      class="mb-8"
                    />
                    <div class="text-xs mb-12" v-else>
                      <span class="fw-medium me-8">{{ $t("general.shared.caseManager") }}:</span>
                      <b class="text-dark">{{ clientTour.case_manager.user?.name }} ({{ clientTour.case_manager.agency?.name }})</b>
                    </div>

                    <div class="row g-2">
                      <div class="col-12 col-xl-6">
                        <v-form
                          id="case-update-init-date"
                          :label="`${$t('general.shared.initDate')}*`"
                          :yup-errors-variable="errors.initDate"
                          form-type="outline"
                          class="mb-0"
                        >
                          <Datepicker
                            v-model="initDate"
                            :day-class="getEndDayClass"
                            :enable-time-picker="false"
                            :format="settings.formatDate"
                            :highlight="highlightedDates"
                            :input-class-name="`form-control ${!!errors.initDate ? 'is-invalid' : ''}`"
                            :month-change-on-scroll="false"
                            auto-apply
                            calendar-cell-class-name="init-date-cell"
                            class="form-datepicker"
                            @update:model-value="setHighlightedDates"
                          />

                          <template #errors>
                            <span v-if="!!errors.initDate" class="invalid-feedback d-block">
                              {{ errors.initDate }}
                            </span>
                          </template>
                        </v-form>
                      </div>

                      <div class="col-12 col-xl-6">
                        <v-form
                          id="case-update-end-date"
                          :label="`${$t('general.shared.endDate')}*`"
                          :yup-errors-variable="errors.endDate"
                          form-type="outline"
                          class="mb-0"
                        >
                          <Datepicker
                            v-model="endDate"
                            :day-class="getInitDayClass"
                            :enable-time-picker="false"
                            :format="settings.formatDate"
                            :highlight="highlightedDates"
                            :input-class-name="`form-control ${!!errors.endDate ? 'is-invalid' : ''}`"
                            :min-date="!!initDate ? initDate : null"
                            :month-change-on-scroll="false"
                            :start-date="!!initDate ? initDate : null"
                            auto-apply
                            calendar-cell-class-name="end-date-cell"
                            class="form-datepicker"
                            disabled
                            @update:model-value="setHighlightedDates"
                          />

                          <template #errors>
                            <span v-if="!!errors.endDate" class="invalid-feedback d-block">
                              {{ errors.endDate }}
                            </span>
                          </template>
                        </v-form>
                      </div>
                    </div>
                    <div class="row mt-2">
                      <div class="col-12 col-xl-12">
                        <v-form
                          id="case-update-alternate-start-date"
                          :label="`${$t('general.shared.alternativeStartDate')}`"
                          :yup-errors-variable="errors.alternateStartDate"
                          form-type="outline"
                          class="mb-0"
                        >
                          <Datepicker
                            v-model="alternateStartDate"
                            :enable-time-picker="false"
                            :format="settings.formatDate"
                            :input-class-name="`form-control ${!!errors.alternateStartDate ? 'is-invalid' : ''}`"
                            :month-change-on-scroll="false"
                            auto-apply
                            calendar-cell-class-name="alternate-start-date-cell"
                            class="form-datepicker"
                          />

                          <template #errors>
                            <span v-if="!!errors.alternateStartDate" class="invalid-feedback d-block">
                              {{ errors.alternateStartDate }}
                            </span>
                          </template>
                        </v-form>
                      </div>
                    </div>
                    <div class="row mt-2">
                      <div class="col-12 col-xl-12">
                        <v-form
                          id="case-update-client-drive-url"
                          :label="`${$t('general.shared.clientDriveUrl')}`"
                          form-type="outline"
                          class="mb-0"
                        >
                          <v-form-input
                            id="agency-create-nif"
                            v-model="clientDriveUrl"
                            autocomplete="off"
                            class="mb-0"
                            form-type="outline"
                          />
                        </v-form>
                      </div>
                    </div>
                  </fieldset>

                  <div class="d-flex justify-content-end gap-8 mt-12">
                    <v-button
                      v-if="isPreCase"
                      :disabled="userStore.caseIsSold"
                      class="btn-icon w-fit align-self-end rounded-sm setup-action-btn action-update-wizard"
                      size="sm"
                      variant="outline-dark"
                      @click="openUpdateWizard"
                    >
                      <v-icon icon="compass" size="xs" space="me-8" />
                      {{ $t("template.editCaseWithWizard") }}
                    </v-button>
                    <v-button
                      :is-loading="accepted"
                      :disabled="!meta.valid || accepted || !meta.dirty || disableButton"
                      class="btn-icon w-fit align-self-end rounded-sm setup-action-btn action-save"
                      size="sm"
                      variant="primary"
                      @click="updateInitialTourDate"
                    >
                      {{ $t("general.button.save") }}
                      <v-icon icon="arrow-right" size="xs" space="ms-8" />
                    </v-button>
                  </div>
                </form>
                <div class="search-separator-container pb-16 mb-12" v-if="clientTour.additionalRequirements">
                  <div class="separator-text-container">
                    <span class="separator-text text-xs">{{ $t("clientTour.additionalInfo") }}</span>
                  </div>
                </div>
                <fieldset v-if="clientTour.additionalRequirements" class="mb-12">
                  <legend class="visually-hidden">{{ $t("clientTour.additionalRequirements") }}</legend>
                  <div class="form-outline">
                    <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                      <span>{{ $t("clientTour.additionalRequirements") }}</span>
                    </label>

                    <div
                      id="case-tour-additional-req-text"
                      class="html-editor-display text-sm"
                      v-html="clientTour.additionalRequirements"
                    ></div>
                  </div>
                </fieldset>
                <fieldset v-if="clientTour.otherRoomRequirements" class="mb-12">
                  <legend class="visually-hidden">{{ $t("clientTour.otherRoomRequirements") }}</legend>

                  <div class="form-outline">
                    <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                      <span>{{ $t("clientTour.otherRoomRequirements") }}</span>
                    </label>

                    <div
                      id="case-tour-other-room-text"
                      class="html-editor-display text-sm"
                      v-html="clientTour.otherRoomRequirements"
                    ></div>
                  </div>
                </fieldset>

                <div class="search-separator-container pb-16 mb-12">
                  <div class="separator-text-container">
                    <span class="separator-text text-xs">{{ $t("clientTour.tourBasicInformation") }}</span>
                  </div>
                </div>

                <the-case-setup-tour-details-v2
                  :agency="agency.slug"
                  :client-case-id="Number(clientTour.id)"
                  :case="tour"
                  @reload-case="$emit('reloadClientTour')"
                />
              </div>
            </div>
          </div>
        </div>

        <the-case-setup-tour-contacts-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :case="tour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-tour-terms-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-tour-complementary-text-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-before-tour-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-luggage-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-featured-image-v2
          :agency="agency.slug"
          :client-tour="clientTour.id"
          :tour="currentTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <div id="accordionArchive" class="accordion">
          <div class="case-details accordion-item">
            <h2 id="headingArchive" class="accordion-header">
              <button
                aria-controls="collapseArchive"
                aria-expanded="false"
                class="accordion-button collapsed"
                data-bs-target="#collapseArchive"
                data-bs-toggle="collapse"
                type="button"
              >
                <div class="mb-0 d-flex align-items-center">
                  <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
                    <v-icon icon="import" size="xs" class="text-white" />
                  </div>

                  <div>
                    <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.archiveTitle") }}</div>
                    <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.archiveSubtitle") }}</div>
                  </div>
                </div>
              </button>
            </h2>

            <div
              id="collapseArchive"
              aria-labelledby="headingArchive"
              class="accordion-collapse collapse"
              data-bs-parent="#accordionArchive"
            >
              <div class="accordion-body d-flex flex-column pb-16 pb-xl-24">
                <div class="alert alert-warning d-flex align-items-center mb-12" role="alert">
                  <v-icon icon="info" size="md" space="me-12" />

                  <div class="text-xs">
                    {{ $t("clientTour.archiveText") }}
                  </div>
                </div>

                <v-button
                  v-if="!clientTour.deletedAt"
                  id="btn-archive-client-tour"
                  :disabled="disableArchiveBtn"
                  class="btn-icon mb-4 align-self-end mt-12 rounded-sm setup-action-btn action-archive"
                  size="xs"
                  variant="danger"
                  @click="archiveClientTour"
                >
                  <v-icon icon="import" space="me-8" />
                  <template v-if="disableArchiveBtn">{{ $t("general.button.archiving") }}</template>
                  <template v-else>{{ $t("general.button.archive") }}</template>
                </v-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid right">
        <the-case-setup-notifications-v2
          :agency="agency.slug"
          :client-tour="Number(clientTour.id)"
          :tour="Number(caseId)"
          :client-notification-logs="tour?.client_notification_logs"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-notification-log-v2
          :agency="agency.slug"
          :client-tour="Number(clientTour.id)"
          :supplier-notification-logs="tour?.supplier_notification_logs"
          :tour="Number(caseId)"
        />

        <the-case-setup-pax-v2
          :agency="agency.slug"
          :client-tour="clientTour"
          :tour="Number(caseId)"
          :client="{
            traveller_id: tour?.client?.id,
            name: `${tour?.client?.name} ${tour?.client?.surname}`,
            email: tour?.client?.email,
          }"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-quotes-v2 :agency="agency.slug" :client-tour="Number(clientTour.id)" :tour="currentTour" />

        <the-case-setup-after-tour-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-include-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-not-include-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />

        <the-case-setup-optional-v2
          :agency="agency.slug"
          :case-id="Number(caseId)"
          :client-tour="clientTour"
          @reload-case="$emit('reloadClientTour')"
        />
      </div>
    </div>

    <the-client-details-offcanvas
      v-if="offCanvasToShow === 'the-client-details-offcanvas'"
      :client-data="clientTour.client"
      :is-archived="!!clientTour.client.deletedAt"
      @closed="closeOffCanvas"
      @reload-clients="$emit('reloadClientTour')"
    />

    <TheCaseUpdateWizard
      v-if="updateWizardOpened && wizardData"
      :template-data="wizardTemplate"
      :initial-data="wizardData"
      :case-id="Number(caseId)"
      :agency-slug="agency.slug"
      @closed="updateWizardOpened = false"
      @reload-case="onWizardReload"
    />

  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import { addDays, isEqual, set } from "date-fns";
import { VForm, VFormInput } from "@uniqoders/form";
import ClientTour from "@/api/objects/ClientTour";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import useFormValidation from "@/helpers/form";
import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import TheCaseSetupPaxV2 from "@/components/case/V2/TheCaseSetupPaxV2.vue";
import Tour from "@/api/objects/Tour";
import TheCaseSetupTourDetailsV2 from "@/components/case/V2/TheCaseSetupTourDetailsV2.vue";
import { useUserStore } from "@/stores/user";
import VSelectUserCaseSearch from "@/components/vendor/selectSearch/VSelectUserCaseSearch.vue";
import TheCaseSetupTourContactsV2 from "@/components/case/V2/TheCaseSetupTourContactsV2.vue";
import TheCaseSetupNotificationsV2 from "@/components/case/V2/TheCaseSetupNotificationsV2.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheClientDetailsOffcanvas from "@/components/client/TheClientDetailsOffcanvas.vue";
import TheCaseSetupTourTermsV2 from "@/components/case/V2/TheCaseSetupTourTermsV2.vue";
import TheCaseSetupTourComplementaryTextV2 from "@/components/case/V2/TheCaseSetupTourComplementaryV2.vue";
import TheCaseSetupQuotesV2 from "@/components/case/V2/TheCaseSetupQuotesV2.vue";
import TheCaseSetupFeaturedImageV2 from "@/components/case/V2/TheCaseSetupFeaturedImageV2.vue";
import { useAppStore } from "@/stores/app";
import TheCaseSetupNotificationLogV2 from "@/components/case/V2/TheCaseSetupNotificationLogV2.vue";
import TheCaseSetupAfterTourV2 from "@/components/case/V2/TheCaseSetupAfterTourV2.vue";
import TheCaseSetupBeforeTourV2 from "@/components/case/V2/TheCaseSetupBeforeTourV2.vue";
import TheCaseSetupLuggageV2 from "@/components/case/V2/TheCaseSetupLuggageV2.vue";
import TheCaseSetupIncludeV2 from "@/components/case/V2/TheCaseSetupIncludeV2.vue";
import TheCaseSetupNotIncludeV2 from "@/components/case/V2/TheCaseSetupNotIncludeV2.vue";
import TheCaseSetupOptionalV2 from "@/components/case/V2/TheCaseSetupOptionalV2.vue";
import TheCaseUpdateWizard from "@/components/case/V2/TheCaseUpdateWizard.vue";
import { isPreCaseStatus } from "@/api/objects/CaseConstants";
import { useToast } from "vue-toastification";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";

const props = defineProps<{
  caseId: string;
  clientTour: ClientTour;
}>();

const emit = defineEmits<{
  (e: "reloadClientTour"): void;
}>();

const { t } = useI18n();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const toast = useToast();
const authStore = useAuthStore();
const userStore = useUserStore();
const appStore = useAppStore();
const router = useRouter();

const { agency } = storeToRefs(authStore);
const { currentTour } = storeToRefs(userStore);
const { settings } = storeToRefs(appStore);

const rules = yup.object({
  initDate: yup.string().required().nullable().label(t("general.shared.initDate")),
  endDate: yup.string().required().nullable().label(t("general.shared.endDate")),
  alternateStartDate: yup.string().nullable().label(t("general.shared.alternativeStartDate")),
  clientId: yup.number().required().nullable().label(t("general.shared.client")),
  clientDriveUrl: yup.string().nullable().notRequired().label(t("general.shared.clientDriveUrl")),
  partyName: yup
    .string()
    .nullable()
    .test(
      'party-name-required-for-agency',
      t('clientTour.partyNameRequired'),
      (value) => {
        if (props.clientTour?.client?.type !== 'agency') return true;
        return !!value;
      }
    )
    .label(t('clientTour.partyName')),
});

const initialValues: Record<string, any> = {
  clientId: !!props.clientTour.clientId ? props.clientTour.clientId : 0,
  initDate: props.clientTour.start_date,
  endDate: props.clientTour.end_date,
  alternateStartDate: props.clientTour.alt_start_date,
  clientDriveUrl: props.clientTour.client_drive_url,
  partyName: props.clientTour.party_name ?? props.clientTour.partyName ?? '',
};

const form = useFormValidation(rules, initialValues);
const { accepted, meta, errors, setFieldValue } = form;

const { value: initDate } = useField<Date>("initDate");
const { value: endDate } = useField<Date>("endDate");
const { value: alternateStartDate } = useField<Date>("alternateStartDate");
const { value: clientId } = useField<number | null>("clientId");
const { value: clientDriveUrl } = useField<string>("clientDriveUrl");
const { value: partyName } = useField<string>("partyName");

const highlightedDates = ref<Date[]>([]);
const disableButton = ref(false);
const disableArchiveBtn = ref(false);
const tour = ref<null | undefined | Tour>(null);
const userId = ref<null | number | string>(null);

// Update wizard state
const updateWizardOpened = ref(false);
const wizardData = ref<Record<string, any> | null>(null);
const wizardTemplate = ref<Record<string, any> | null>(null);

const isPreCase = computed(() => isPreCaseStatus(props.clientTour));

const openUpdateWizard = async () => {
  try {
    const response = await api.case.getWizardData(agency.value.slug, Number(props.caseId));
    wizardData.value = response?.content ?? response;

    // Build minimal template object from case data; the wizard will
    // fetch the full activity details on mount via template.value?.id
    wizardTemplate.value = {
      id: wizardData.value?.activity_id,
      name: props.clientTour?.name,
    };

    updateWizardOpened.value = true;
  } catch {
    toast.error(t("general.shared.somethingWentWrong"));
  }
};

const onWizardReload = () => {
  updateWizardOpened.value = false;
  wizardData.value = null;
  wizardTemplate.value = null;
  emit("reloadClientTour");
};
const hasSpecialNeeds = computed(() => {
  return (
    !!props.clientTour?.additionalRequirements ||
    (!!props.clientTour?.invoice_notes && (props.clientTour.invoice_notes.includes("Special Requests") || props.clientTour.invoice_notes.includes("Transfer Needs")))
  );
});

const parsedSpecialRequests = computed(() => {
  if (!props.clientTour?.invoice_notes) return null;
  const match = props.clientTour.invoice_notes.match(/Special Requests: (.*?)(?:\n|$)/);
  return match && match[1] !== 'None' ? match[1] : null;
});

const parsedTransferNeeds = computed(() => {
  if (!props.clientTour?.invoice_notes) return null;
  const match = props.clientTour.invoice_notes.match(/Transfer Needs: (.*?)(?:\n|$)/);
  return match && match[1] !== 'None' ? match[1] : null;
});

const originatedLink = computed(() => {
  return router.resolve({
    name: "case-v2.setup.show",
    params: { caseId: props.clientTour?.parent_id },
  }).href;
});

const clientName = computed(() => {
  let clientName = "";

  if (props.clientTour?.client?.name) {
    clientName += `(${props.clientTour.client.code}) ${props.clientTour.client.name}`;

    if (props.clientTour.client.surname) {
      clientName += ` ${props.clientTour.client.surname}`;
    }
  }

  return clientName;
});

onMounted(() => {
  userId.value = props.clientTour?.case_manager?.user_id;

  tour.value = props.clientTour || null;

  setHighlightedDates();
});

const getInitDayClass = (date: Date) => {
  if (!initDate.value) return "";

  const normalizedInitDate = set(new Date(initDate.value), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  return isEqual(date, normalizedInitDate) ? "init-marked-cell" : "";
};

const getEndDayClass = (date: Date): string => {
  if (!endDate.value) return "";

  const normalizedEndDate = set(new Date(endDate.value), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  return isEqual(date, normalizedEndDate) ? "end-marked-cell" : "";
};

const setHighlightedDates = () => {
  if (initDate.value && endDate.value) {
    const start = new Date(initDate.value);
    const end = new Date(endDate.value);

    const dates: Date[] = [];
    let date = start;

    while (date <= end) {
      dates.push(new Date(date)); // ensure new Date instance is pushed
      date = addDays(date, 1);
    }

    highlightedDates.value = dates;
  } else {
    highlightedDates.value = [];
  }
};

const updateInitialTourDate = async () => {
  const originalDate = props.clientTour.start_date
    ? set(new Date(props.clientTour.start_date), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }).getTime()
    : null;
  const currentDate = initDate.value
    ? set(new Date(initDate.value as any), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }).getTime()
    : null;

  if (originalDate !== null && currentDate !== null && originalDate === currentDate) {
    await doUpdateInitialTourDate();
    return;
  }

  await $modal?.confirm({
    title: t("clientTour.dateModal.title"),
    text: t("clientTour.dateModal.text"),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.confirm"),
    confirmButtonCallback: doUpdateInitialTourDate,
  });
};

const doUpdateInitialTourDate = async () => {
  let saved = false;
  try {
    accepted.value = true;
    disableButton.value = true;

    const data = {
      case_manager_id: tour.value?.case_manager_id,
      start_date: initDate.value,
      alt_start_date: alternateStartDate.value,
      tour_name: tour.value?.tour_name || tour.value?.name,
      travel_type: tour.value?.travel_type,
      origin_id: tour.value?.origin_id,
      destination_id: tour.value?.destination_id,
      client_drive_url: clientDriveUrl.value ?? "",
      party_name: props.clientTour?.client?.type === 'agency' ? partyName.value : null,
    };

    if (agency?.value?.slug) {
      await api.case.updateBasicInfoCase(agency.value.slug, props.clientTour.id, data);
      toast.success(t("general.shared.savedChanges"));
      saved = true;
    }
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || t("general.error"));
  } finally {
    disableButton.value = false;
    accepted.value = false;
  }

  if (saved) {
    emit("reloadClientTour");
  }
};

const doArchiveClientTour = async () => {
  try {
    if (agency?.value?.slug) {
      disableArchiveBtn.value = true;

      await api.case.archiveCase(agency.value.slug, props.clientTour.id);

      toast.success(t("general.shared.savedChanges"));

      await router.replace({ name: "case-v2.index" });
    }
  } catch (e: any) {
    console.error(e?.response?.data);
    toast.error(e?.response?.data?.message || t("general.error"));
  } finally {
    disableArchiveBtn.value = false;
  }
};

const archiveClientTour = async () => {
  await $modal?.delete({
    title: t("clientTour.archiveTitle"),
    text: t("clientTour.archiveAlertText", { case: props.clientTour.name }),
    deleteButtonText: t("general.button.archive"),
    deleteButtonLoadingText: t("general.button.archiving"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: doArchiveClientTour,
  });
};

const assignUserToCase = async () => {
  try {
    if (!agency?.value?.slug || !props.clientTour?.id) return;

    const data = {
      case_manager_id: userId.value,
      start_date: initDate.value,
      alt_start_date: alternateStartDate.value,
      tour_name: tour.value?.tour_name || tour.value?.name,
      travel_type: tour.value?.travel_type,
      origin_id: tour.value?.origin_id,
      destination_id: tour.value?.destination_id,
      client_drive_url: clientDriveUrl.value ?? "",
      party_name: props.clientTour?.client?.type === 'agency' ? partyName.value : null,
    };


    if (userId.value) {
      await api.case.updateBasicInfoCase(agency.value.slug, props.clientTour.id, data);
      toast.success(t("general.shared.savedChanges"));
      emit("reloadClientTour");
    }
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || t("general.error"));
  }
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: 0.75rem 2rem;
}

.wrapper {
  grid-template-columns: 1fr;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
}

.grid.left {
  grid-column: 1 / span 1;
  grid-auto-rows: min-content;
}
.grid.right {
  grid-column: span 1 / -1;
  grid-auto-rows: min-content;
}

.accordion-item {
  background-color: transparent;
}

:deep(.accordion-button) {
  padding: 0.5rem 1rem !important;
  background-color: transparent !important;
  font-size: 1rem;
}

.html-editor-display {
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 4px;
  background-color: #fff;
}

.text-xs {
  font-size: 0.75rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}

:deep(.btn-icon:not(.rounded-circle)) {
  border-radius: 6px !important;
}

.setup-action-btn {
  border-radius: 6px !important;
  font-size: 12px !important;
  letter-spacing: 0.3px;
  padding: 0.45rem 0.9rem !important;
}

.action-save {
  color: #ffffff !important;
}

.action-save:hover {
  background-color: #45b478 !important;
  border-color: #45b478 !important;
}

.action-update-wizard {
  color: #222422 !important;
}

.action-archive {
  background-color: #FD0C6F !important;
  border-color: #FD0C6F !important;
  color: #ffffff !important;
}

.action-archive:hover {
  background-color: #dc095f !important;
  border-color: #dc095f !important;
}
</style>
